<script lang="ts">
  import { onDestroy } from 'svelte';
  import QRCode from 'qrcode';
  import { identity } from '$crypto/identity.svelte';
  import { pair, fmtCooldown, MAX_TWINS, type PairRecord } from '$stores/pair.svelte';
  import { profile } from '$stores/profile.svelte';
  import { twinsDrawer } from '$stores/twinsDrawer.svelte';
  import { generateNickname, sanitizeHandle } from '$data/nicknames';
  import { b64uDecode } from '$crypto/encryption';
  import { identiconSvg } from '$game/identicon';
  import { announceBond } from '$game/bond-inbox';
  import { shareInvite, buildBondLink } from '$lib/share';

  let friendCode = $state('');
  let friendHandle = $state('');
  let toast = $state('');
  let toastVisible = $state(false);
  let qrSvg = $state<string>('');
  let qrVisible = $state(false);

  /** Tick once a minute so cooldown badges count down live. Only runs while
   *  the drawer is open so we don't burn an interval forever. */
  let tick = $state(0);
  let timer: ReturnType<typeof setInterval> | null = null;

  function startTick() {
    if (timer) return;
    timer = setInterval(() => (tick += 1), 60_000);
  }

  function stopTick() {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  }

  onDestroy(() => stopTick());

  /** React to drawer open state — hydrate state, prefill, manage timer. */
  $effect(() => {
    if (twinsDrawer.open) {
      identity.ensureLoaded();
      pair.hydrate();
      profile.hydrate();
      if (!profile.handle) profile.setHandle(generateNickname());
      if (twinsDrawer.prefillCode) friendCode = twinsDrawer.prefillCode;
      if (twinsDrawer.prefillHandle) friendHandle = twinsDrawer.prefillHandle;
      startTick();
    } else {
      stopTick();
      qrVisible = false;
      qrSvg = '';
    }
  });

  /** Handle is part of the link, so the derived URL must read both. */
  const bondLink = $derived.by(() => {
    void profile.handle;
    void identity.publicKeyB64;
    return buildBondLink();
  });

  /** Render the QR code as inline SVG only when the user reveals it. */
  $effect(() => {
    const url = bondLink;
    if (!url || !qrVisible) {
      qrSvg = '';
      return;
    }
    QRCode.toString(url, {
      type: 'svg',
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#ffffff', light: '#00000000' }
    })
      .then((svg) => (qrSvg = svg))
      .catch(() => (qrSvg = ''));
  });

  /** Up-to-`MAX_TWINS` newest-first records for the list. */
  const visibleTwins = $derived.by<PairRecord[]>(() => pair.active.slice(0, MAX_TWINS));

  const atLimit = $derived(pair.active.length >= MAX_TWINS);

  function avatarFor(rec: PairRecord, size = 34): string {
    try {
      return identiconSvg(b64uDecode(rec.pubkeyB64), size);
    } catch {
      return identiconSvg(null, size);
    }
  }

  function flash(msg: string) {
    toast = msg;
    toastVisible = true;
    setTimeout(() => (toastVisible = false), 1700);
  }

  function pickTwin(rec: PairRecord) {
    pair.setActive(rec.pairId);
    flash(`Now playing with @${rec.handle}`);
    // Closing on selection feels right for a slide-in drawer — the user
    // came in to choose a twin, so let them get back to the game.
    setTimeout(() => twinsDrawer.close(), 250);
  }

  function unbond(rec: PairRecord) {
    pair.unbond(rec.pairId);
    flash(`Unbonded @${rec.handle}`);
  }

  function submitBond() {
    if (!friendCode.trim()) {
      flash("Paste your friend's bond code");
      return;
    }
    if (atLimit) {
      flash(`Twin limit reached (${MAX_TWINS}). Unbond someone first.`);
      return;
    }
    const handle = sanitizeHandle(friendHandle || 'twin');
    const code = friendCode.trim();
    const id = pair.bond(code, handle || 'twin');
    if (!id) {
      flash('Bond code looks invalid');
      return;
    }
    // Fire-and-forget — let the friend's app pick this up on their next
    // poll so the bond is bidirectional automatically.
    void announceBond(code);
    // Trigger the full-screen celebration overlay (audio + visual).
    // +page.svelte owns the listener so a single component drives the
    // ceremony for both the initiator (here) and auto-bond receivers.
    window.dispatchEvent(
      new CustomEvent('vutwin:bondcelebration', {
        detail: { handle: handle || 'twin' }
      })
    );
    friendCode = '';
    friendHandle = '';
  }

  async function shareNow() {
    const out = await shareInvite();
    if (out.ok && out.used === 'web-share') return;
    if (out.ok && out.used === 'clipboard') flash('Invite link copied');
    else if (out.used === 'noop') flash('Generate your bond code first');
  }

  /** One-tap "paste from clipboard, then bond". If the textarea is empty
   *  we try to read the clipboard so the user doesn't have to long-press →
   *  paste before they can link. If clipboard access is denied (Safari
   *  insecure context, permission prompt declined, …) we fall through to
   *  the empty-field flash so the user can still paste manually. */
  async function pasteAndLink() {
    if (atLimit) {
      flash(`Twin limit reached. Unbond someone first.`);
      return;
    }
    if (!friendCode.trim()) {
      try {
        const text = await navigator.clipboard.readText();
        if (text && text.trim()) friendCode = text.trim();
      } catch {
        /* clipboard-read may be blocked — manual paste still works. */
      }
    }
    submitBond();
  }

  function close() {
    twinsDrawer.close();
  }

  function onKey(e: KeyboardEvent) {
    if (twinsDrawer.open && e.key === 'Escape') close();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if twinsDrawer.open}
  <button
    class="td-backdrop"
    type="button"
    aria-label="Close twins drawer"
    onclick={close}
  ></button>
{/if}

<div
  class="td-panel"
  class:open={twinsDrawer.open}
  role="dialog"
  aria-modal="true"
  aria-label="Your twins"
  aria-hidden={!twinsDrawer.open}
  inert={!twinsDrawer.open}
>
  <div class="td-grabber" aria-hidden="true"></div>

  <header class="td-head">
    <div class="td-title">YOUR TWINS</div>
    <div class="td-count">{pair.active.length}/{MAX_TWINS}</div>
    <button class="td-close" type="button" aria-label="Close" onclick={close}>×</button>
  </header>

  <div class="td-body">
    <section class="td-section">
      <div class="td-section-title">PICK A TWIN</div>
      {#key tick}
        {#if visibleTwins.length === 0}
          <div class="td-empty">
            <div class="td-empty-title">No twins yet</div>
            <p class="td-empty-sub">Share your code to invite one.</p>
          </div>
        {:else}
          <ul class="td-list" role="radiogroup" aria-label="Choose a twin">
            {#each visibleTwins as rec (rec.pairId)}
              {@const cooldown = pair.cooldownRemainingMs(rec.pairId)}
              {@const isActive = pair.activeId === rec.pairId}
              <li class="td-row" class:active={isActive} class:cooldown={cooldown > 0}>
                <button
                  type="button"
                  class="td-row-main"
                  role="radio"
                  aria-checked={isActive}
                  onclick={() => pickTwin(rec)}
                >
                  <span class="td-avatar">{@html avatarFor(rec)}</span>
                  <span class="td-row-info">
                    <span class="td-row-handle">@{rec.handle}</span>
                    {#if cooldown > 0}
                      <span class="td-row-status cooldown">
                        Cooldown · {fmtCooldown(cooldown)}
                      </span>
                    {:else}
                      <span class="td-row-status ready">Ready</span>
                    {/if}
                  </span>
                  {#if isActive}
                    <span class="td-active-badge" aria-label="Currently selected">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  {/if}
                </button>
                <button
                  type="button"
                  class="td-row-remove"
                  aria-label="Unbond {rec.handle}"
                  onclick={() => unbond(rec)}
                >
                  ×
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      {/key}
    </section>

    <section class="td-section">
      <div class="td-section-title">
        ADD A TWIN
        {#if atLimit}<span class="td-limit-pill">LIMIT REACHED</span>{/if}
      </div>

      <div class="td-card">
        <div class="td-card-label">YOUR BOND CODE</div>
        <div class="td-bond-code">{identity.publicKeyB64 || '…'}</div>
        <button
          type="button"
          class="td-qr-toggle"
          onclick={() => (qrVisible = !qrVisible)}
          aria-expanded={qrVisible}
          aria-controls="td-qr-region"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <path d="M14 14h3v3M21 14v.01M14 21h.01M17 17h.01M21 17v4M17 21h4" />
          </svg>
          {qrVisible ? 'Hide QR code' : 'Show QR code'}
          <span class="td-qr-chevron" class:open={qrVisible} aria-hidden="true">▾</span>
        </button>
        <div id="td-qr-region" class="td-qr-region" class:open={qrVisible}>
          {#if qrVisible}
            {#if qrSvg}
              <div class="td-qr-wrap" aria-label="Bond invite QR code">{@html qrSvg}</div>
            {:else}
              <div class="td-qr-placeholder" role="status" aria-live="polite">Generating QR…</div>
            {/if}
          {/if}
        </div>
        <button
          type="button"
          class="btn btn-primary td-share-btn"
          onclick={shareNow}
          aria-label="Share my bond code"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          Share my code
        </button>
      </div>

      <div class="td-card" class:disabled={atLimit}>
        <div class="td-card-label">FRIEND'S BOND CODE</div>
        <textarea
          rows="2"
          bind:value={friendCode}
          placeholder="Paste their code here…"
          spellcheck="false"
          autocapitalize="off"
          autocomplete="off"
          disabled={atLimit}
        ></textarea>
        <input
          type="text"
          bind:value={friendHandle}
          placeholder="Their handle (optional)"
          maxlength="18"
          spellcheck="false"
          autocapitalize="off"
          autocomplete="off"
          disabled={atLimit}
        />
        <button
          type="button"
          class="btn btn-primary td-bond-btn"
          onclick={pasteAndLink}
          disabled={atLimit}
        >
          {atLimit ? 'Twin limit reached' : 'Paste & Link'}
        </button>
      </div>
    </section>
  </div>

  {#if toastVisible}
    <div class="td-toast" role="status" aria-live="polite">{toast}</div>
  {/if}
</div>

<style>
  .td-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(5, 0, 16, 0.55);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    border: 0;
    padding: 0;
    margin: 0;
    z-index: 30;
    cursor: pointer;
    animation: td-fade 180ms var(--spring-press) both;
  }
  :global(body.brutal) .td-backdrop {
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  @keyframes td-fade {
    from {
      opacity: 0;
    }
  }

  /* ============ iOS-style bottom sheet ============
     Anchors to the bottom edge, spans the full width on phones (capped on
     tablets/desktops), and slides up with the canonical Apple sheet curve
     `cubic-bezier(0.32, 0.72, 0, 1)` — the same easing used by UIKit
     bottom sheets so the entrance feels native rather than web. The
     pre-open transform is `translateY(101%)` instead of 100% to avoid a
     1px sliver of the sheet bleeding into the safe-area on devices with
     fractional pixel ratios. */
  .td-panel {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0 auto;
    width: 100%;
    max-width: 560px;
    max-height: 88dvh;
    z-index: 40;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    border: 1px solid var(--border);
    border-bottom: none;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -18px 60px rgba(0, 0, 0, 0.6);
    transform: translateY(101%);
    transition: transform 280ms cubic-bezier(0.32, 0.72, 0, 1);
    will-change: transform;
    padding-bottom: max(var(--safe-bottom, 0px), 8px);
    overscroll-behavior: contain;
  }
  .td-panel.open {
    transform: translateY(0);
  }
  :global(body.brutal) .td-panel {
    border: 2px solid var(--text);
    border-bottom: none;
    border-radius: 0;
    box-shadow: 0 -6px 0 0 var(--text);
  }
  @media (prefers-reduced-motion: reduce) {
    .td-panel {
      transition: none;
    }
    .td-backdrop {
      animation: none;
    }
  }

  /* iOS grabber pill — purely visual affordance that this surface came
     from below and is dismissable. Not interactive (close button + the
     backdrop tap already handle dismiss). */
  .td-grabber {
    flex: 0 0 auto;
    width: 38px;
    height: 5px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.2);
    margin: 8px auto 4px;
  }
  :global(body.brutal) .td-grabber {
    background: var(--text);
    border-radius: 0;
  }

  .td-head {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
  }
  :global(body.brutal) .td-head {
    border-bottom-width: 2px;
  }
  .td-title {
    flex: 1;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .td-count {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: var(--text-3);
    background: var(--surface);
    border: 1px solid var(--border);
    padding: 3px 6px;
    border-radius: var(--radius-sm);
  }
  :global(body.brutal) .td-count {
    border-width: 2px;
  }
  .td-close {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text);
    font-size: 20px;
    line-height: 1;
    display: grid;
    place-items: center;
  }
  :global(body.brutal) .td-close {
    border-width: 2px;
  }
  .td-close:hover {
    background: var(--surface-2);
  }

  .td-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 10px 12px calc(14px + var(--safe-bottom, 0px));
    display: flex;
    flex-direction: column;
    gap: 12px;
    -webkit-overflow-scrolling: touch;
  }
  .td-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .td-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    color: var(--text-3);
    text-transform: uppercase;
  }
  .td-limit-pill {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: var(--accent-pink);
    background: rgba(255, 56, 188, 0.12);
    border: 1px solid var(--border-warm);
    padding: 3px 7px;
    border-radius: var(--r-pill);
  }
  :global(body.brutal) .td-limit-pill {
    color: var(--text);
    background: var(--bg);
    border-width: 2px;
  }

  .td-empty {
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }
  :global(body.brutal) .td-empty {
    border-style: solid;
    border-width: 2px;
  }
  .td-empty-title {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .td-empty-sub {
    font-size: 11px;
    color: var(--text-2);
    line-height: 1.4;
  }

  .td-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .td-row {
    display: flex;
    align-items: stretch;
    gap: 6px;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    transition: var(--transition);
  }
  :global(body.brutal) .td-row {
    border-width: 2px;
  }
  .td-row:hover {
    background: var(--surface-2);
  }
  .td-row.active {
    border-color: var(--cyan);
    background: rgba(0, 220, 255, 0.06);
    box-shadow: 0 0 0 1px rgba(0, 220, 255, 0.35);
  }
  :global(body.brutal) .td-row.active {
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
    background: var(--bg);
  }
  .td-row.cooldown {
    opacity: 0.7;
  }
  .td-row.cooldown.active {
    opacity: 0.9;
  }
  .td-row-main {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: var(--text);
    min-width: 0;
  }
  .td-avatar {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    display: grid;
    place-items: center;
  }
  :global(body.brutal) .td-avatar {
    border-width: 2px;
  }
  .td-avatar :global(svg) {
    display: block;
    width: 100%;
    height: 100%;
  }
  .td-row-info {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .td-row-handle {
    font-size: 13px;
    font-weight: 800;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .td-row-status {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .td-row-status.ready {
    color: var(--accent-green);
  }
  .td-row-status.cooldown {
    color: var(--text-3);
  }
  :global(body.brutal) .td-row-status.ready {
    color: var(--text);
  }
  .td-active-badge {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--cyan);
    color: var(--text-on-light);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  :global(body.brutal) .td-active-badge {
    border-radius: 0;
    background: var(--text);
    color: var(--bg);
  }
  .td-active-badge svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
  }
  .td-row-remove {
    width: 30px;
    flex-shrink: 0;
    background: transparent;
    color: var(--text-3);
    font-size: 18px;
    line-height: 1;
    border-left: 1px solid var(--border);
    cursor: pointer;
    transition: var(--transition);
  }
  :global(body.brutal) .td-row-remove {
    border-left-width: 2px;
  }
  .td-row-remove:hover {
    background: var(--surface-2);
    color: var(--accent-pink);
  }

  .td-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: var(--transition);
  }
  :global(body.brutal) .td-card {
    border-width: 2px;
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .td-card.disabled {
    opacity: 0.5;
  }
  .td-card-label {
    font-size: 9px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
  }
  .td-bond-code {
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', 'Courier New', monospace;
    font-size: 10px;
    color: var(--cyan);
    word-break: break-all;
    padding: 8px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    line-height: 1.35;
    max-height: 56px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }
  :global(body.brutal) .td-bond-code {
    color: var(--text);
    border-width: 2px;
  }

  .td-qr-toggle {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: var(--bg-elev);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-2);
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: var(--transition);
  }
  .td-qr-toggle:hover {
    background: var(--surface-2);
    color: var(--text);
    border-color: var(--cyan);
  }
  .td-qr-toggle[aria-expanded='true'] {
    border-style: solid;
    border-color: var(--cyan);
    color: var(--text);
  }
  :global(body.brutal) .td-qr-toggle {
    border-style: solid;
    border-width: 2px;
  }
  .td-qr-toggle svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
  }
  .td-qr-chevron {
    margin-left: auto;
    font-size: 10px;
    transition: transform 200ms var(--spring-bouncy);
  }
  .td-qr-chevron.open {
    transform: rotate(180deg);
  }
  .td-qr-region {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 240ms var(--spring-press);
  }
  .td-qr-region.open {
    grid-template-rows: 1fr;
  }
  .td-qr-region > * {
    overflow: hidden;
    min-height: 0;
  }
  .td-qr-placeholder {
    margin: 4px auto;
    width: clamp(140px, 42vw, 168px);
    height: clamp(140px, 42vw, 168px);
    display: grid;
    place-items: center;
    background: var(--bg-elev);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-3);
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 700;
  }
  .td-qr-wrap {
    margin: 4px auto;
    width: clamp(140px, 42vw, 168px);
    height: clamp(140px, 42vw, 168px);
    background: var(--bg-elev);
    padding: 6px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
  }
  :global(body.brutal) .td-qr-wrap {
    border-width: 2px;
    background: var(--bg);
  }
  .td-qr-wrap :global(svg) {
    display: block;
    width: 100%;
    height: 100%;
    shape-rendering: crispEdges;
  }

  /* Single share CTA inside the bond-code card. Full-width primary so
     the action reads as the only thing to do here. Painted in cool
     cyan→indigo (the same palette as the twin mascot + the bond-code
     text) to semantically read "outbound / share" — and crucially to
     visually decouple it from the gold `Bond a twin` CTA on the home
     screen, so the bottom sheet has its own colour identity. */
  .td-share-btn {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 12px;
    font-size: 13px;
    background: linear-gradient(135deg, #00d4ff 0%, #3b82f6 55%, #4f46e5 100%);
    color: #fff;
    border: 2px solid rgba(255, 255, 255, 0.32);
    box-shadow:
      var(--depth-md) #1e3a8a,
      0 0 18px rgba(0, 212, 255, 0.42);
    text-shadow: 0 1px 2px rgba(15, 23, 42, 0.45);
  }
  .td-share-btn:active {
    box-shadow:
      0 0 0 0 #1e3a8a,
      0 0 18px rgba(0, 212, 255, 0.42);
  }
  .td-share-btn svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }
  :global(body.brutal) .td-share-btn {
    background: var(--text);
    color: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
    text-shadow: none;
  }

  textarea,
  input[type='text'] {
    width: 100%;
    background: var(--bg-elev);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', 'Courier New', monospace;
    font-size: 12px;
    resize: vertical;
    outline: none;
  }
  input[type='text'] {
    font-family: inherit;
  }
  textarea:focus,
  input[type='text']:focus {
    border-color: var(--cyan);
  }
  :global(body.brutal) textarea,
  :global(body.brutal) input[type='text'] {
    border-width: 2px;
  }
  textarea:disabled,
  input[type='text']:disabled {
    opacity: 0.5;
  }

  /* "Paste & Link" — emerald→green gradient. Green = "completes the
     bond" / success affordance, distinct from the cyan share CTA above
     it AND from the gold `Bond a twin` CTA on the home screen. The
     three primary actions in the app now read as a clear semantic
     palette: gold = invite, cyan = share, green = link/confirm. */
  .td-bond-btn {
    width: 100%;
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #047857 100%);
    color: #ffffff;
    border: 2px solid rgba(255, 255, 255, 0.32);
    box-shadow:
      var(--depth-md) #064e3b,
      0 0 18px rgba(16, 185, 129, 0.42);
    text-shadow: 0 1px 2px rgba(6, 78, 59, 0.45);
  }
  .td-bond-btn:active {
    box-shadow:
      0 0 0 0 #064e3b,
      0 0 18px rgba(16, 185, 129, 0.42);
  }
  .td-bond-btn:disabled {
    background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
    color: rgba(255, 255, 255, 0.55);
    box-shadow: none;
    text-shadow: none;
    border-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
  :global(body.brutal) .td-bond-btn {
    background: var(--text);
    color: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
    text-shadow: none;
  }
  :global(body.brutal) .td-bond-btn:disabled {
    background: var(--bg);
    color: var(--text-3);
    border-color: var(--text-3);
  }

  .td-toast {
    position: absolute;
    top: 48px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 14px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 11px;
    font-weight: 600;
    box-shadow: var(--shadow-soft);
    z-index: 50;
    pointer-events: none;
  }
  :global(body.brutal) .td-toast {
    border-width: 2px;
  }
</style>
